import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const DocumentContext = createContext();
export const useDocuments = () => useContext(DocumentContext);

export const DocumentProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // 1. Fetch All Documents from Backend
  const fetchDocuments = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token && !user) return;

    setLoading(true);
    try {
      const res = await api.get('/documents');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.documents || []);
      setDocuments(data);
    } catch (err) {
      console.error("Error fetching docs", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 2. Fetch Documents for a specific task
  const fetchTaskDocuments = useCallback(async (taskId) => {
    if (!taskId) return [];
    try {
      const res = await api.get(`/tasks/${taskId}/documents`);
      const docs = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.documents || []);
      setDocuments(prev => {
        const remaining = prev.filter(d => String(d.taskId || d.task_id) !== String(taskId));
        return [...docs, ...remaining];
      });
      return docs;
    } catch (err) {
      console.error("Error fetching task documents:", err);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // 3. Add / Upload Document
  const addDocument = async (formDataPayload) => {
    try {
      const taskId = formDataPayload.get ? formDataPayload.get('taskId') : null;
      const res = await api.post('/documents/upload', formDataPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const createdDoc = res.data?.document || res.data?.data;
      if (createdDoc) {
        setDocuments(prev => [createdDoc, ...prev]);
      }
      if (taskId) {
        await fetchTaskDocuments(taskId);
      } else {
        await fetchDocuments();
      }
      return res.data;
    } catch (err) {
      console.error("Upload document error:", err);
      toast.error(err.response?.data?.error || "Upload failed");
      throw err;
    }
  };

  // 4. Delete Document
  const deleteDocument = async (id) => {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      setDocuments(prev => prev.filter(d => String(d.id) !== String(id)));
    } catch (err) {
      console.error("Delete doc error:", err);
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  // 5. Helpers
  const getDocumentById = (id) => documents.find(d => String(d.id) === String(id));

  const getProjectDocuments = (projectId) => {
    if (!projectId) return [];
    return documents.filter(doc => String(doc.projectId || doc.project_id) === String(projectId));
  };

  const getTaskDocuments = (taskId) => {
    if (!taskId) return [];
    return documents.filter(doc => String(doc.taskId || doc.task_id) === String(taskId));
  };

  return (
    <DocumentContext.Provider value={{ 
      documents, 
      loading, 
      fetchDocuments,
      fetchTaskDocuments,
      addDocument, 
      deleteDocument, 
      getDocumentById, 
      getProjectDocuments, 
      getTaskDocuments 
    }}>
      {children}
    </DocumentContext.Provider>
  );
};